import type { ReactNode } from 'react';

export type ReportSubjectKind = 'aor' | 'incident' | 'asset' | 'custom';

/** Channels a report refresh can notify on. Values match `NotificationType`. */
export type AutoRefreshNotifyChannel = 'inApp' | 'email' | 'sms';

/**
 * `interval` repeats every N minutes; `daily` and `weekly` run at a local time
 * of day.
 */
export type AutoRefreshScheduleType = 'interval' | 'daily' | 'weekly';

/**
 * A role without `scopeId` means every holder of the role (recipients saved
 * before scopes).
 */
export type AutoRefreshNotifyRecipient =
  | { kind: 'user'; userGuid: string }
  | { kind: 'role'; roleId: string; scopeId?: string };

export type GetSitrepsResponse = {
  id: UUID;
  item_id: UUID;
  status: string;
  sitrep_source: string;
  sitrep_source_id: UUID;
  sitrep_template_id: UUID;
  name: string;
  timestamp: string;
  user_guid: UUID;
  group_guid: UUID;
  error_message?: string;
  archived: boolean;
  aor_id: UUID | null;
  description: string | null;
  incident_id: UUID | null;
  /**
   * When approval completed; Current is the latest approved report per
   * incident/AOR.
   */
  approved_at?: string | null;
  spatial_filter?: Record<string, unknown> | null;
  spatial_filter_label?: string | null;
  scope_source?: 'incident' | 'aor' | 'custom' | 'asset' | null;
  /** What the report is about. Null on reports created before subjects existed. */
  subject_kind?: ReportSubjectKind | null;
  /** Stable `resource_set_id` of the asset an asset report is about. */
  asset_id?: UUID | null;
  asset_radius_km?: number | string | null;
  /** Asset name when the report was created; kept if the asset is archived. */
  asset_name?: string | null;
  layout_mode?: 'tabs' | 'document' | 'sitstat' | null;
  sitstat_layout?: {
    left?: string[];
    right?: string[];
    center?: string[];
  } | null;
  is_sitrep: boolean;
  // Update tracking metadata
  updated_at: string | null;
  updated_by_user_guid: UUID | null;
  created_by_user_name: string | null;
  updated_by_user_name: string | null;
  // Approval tracking metadata
  approvals: {
    approved_by_user_guid: UUID;
    approved_by_user_name: string | null;
    approved_at: string | null;
  }[];
  approval_status?: 'DRAFT' | 'PENDING' | 'APPROVED' | 'REJECTED';
  pending_reviewer_names?: string | null;
  rejected_reviewer_names?: string | null;
  auto_refresh?: {
    enabled: boolean;
    intervalMinutes: number;
    /** Interval when unset (schedules saved before daily/weekly). */
    scheduleType?: AutoRefreshScheduleType;
    /** Local `HH:MM` for daily and weekly. */
    timeOfDay?: string | null;
    /** 0 (Sunday) to 6 (Saturday) for weekly. */
    daysOfWeek?: number[];
    /** IANA zone the time of day is in. */
    timezone?: string | null;
    lastRunAt: string | null;
    nextRunAt?: string | null;
    consecutiveFailures?: number;
    lastError?: string | null;
    notifyRecipients?: AutoRefreshNotifyRecipient[];
    /** How recipients are told. In-app only when unset. */
    notifyChannels?: AutoRefreshNotifyChannel[];
    lastRunSummary?: AutoRefreshRunSummary | null;
  } | null;
};

/**
 * What the last auto-refresh run did: analytics first, then AI sections
 * generated from them.
 */
export type AutoRefreshRunSummary = {
  at: string;
  draftSitrepId: string;
  analytics: { refreshed: number; failed: string[] };
  ai: { generated: number; failed: number };
};

export type SitrepViewDataProps = {
  data: GetSitrepsResponse[];
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
};

export enum SitrepViewTabs {
  CurrentSitrep = 'CurrentSITREP',
  Historical = 'Historical',
  Drafts = 'Drafts',
  /** Legacy URL value. The tab is gone; these links open Drafts. */
  Reviews = 'PendingReviews',
}

/**
 * Explicit card surface — determines which capabilities and controls are
 * available on a SITREP card.
 *
 * - **Current** — read-only content + "Draft New Version" button.
 * - **Historical** — read-only content, no draft button.
 * - **Draft** — full editing (add/remove/reorder sections, AI generate, section
 *   settings, file attachments, request approval).
 * - **Review** — approve / reject as primary workflow. Editing is available
 *   intentionally via an explicit "Edit before approving" toggle so that
 *   reviewers can make changes before approval when needed — but the editing
 *   path is deliberately separated from the draft-authoring controls.
 */
export enum SitrepCardSurface {
  Current = 'Current',
  Historical = 'Historical',
  Draft = 'Draft',
  Review = 'Review',
}

export type EditableReportsSitrepCardProps = {
  sitrep: GetSitrepsResponse;
  surface: SitrepCardSurface;
  headerBanner?: ReactNode;
};
